import { AppButton, Sheet } from '@clickguard/design-system'
import { RefundRequestDTO } from 'api-models'
import ChevronDown from '@untitled-ui/icons-react/build/esm/ChevronDown'
import Download01 from '@untitled-ui/icons-react/build/esm/Download01'
import Stars01 from '@untitled-ui/icons-react/build/esm/Stars01'
import moment from 'moment'
import { ReactNode, useState } from 'react'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import { REFUNDS_AGENT_KB_URL, REFUNDS_SHEET_FLOAT_STYLE } from '../constants'

const fmtDate = (s: string | null): string =>
	s ? moment(s).utc().format('MMM D, YYYY') : '—'
const ymd = (s: string | null): string =>
	s ? moment(s).utc().format('YYYY-MM-DD') : '—'

// Kept verbatim from the MVP panel — "no new data/logic" per the ticket; restyle only.
const submittedFormFields = (
	item: RefundRequestDTO
): Array<{ label: string; value: string }> => [
	{ label: 'Contact name', value: 'ClickGuard Support' },
	{ label: 'Company', value: 'ClickGuard' },
	{ label: 'Contact email', value: 'umair@clickguard.com' },
	{ label: 'Account login email', value: 'umair@clickguard.com' },
	{ label: 'Customer ID', value: item.customer_id },
	{ label: 'Issue type', value: 'Suspicious activity from IP sources' },
	{ label: 'What is the issue?', value: 'Multiple clicks from the same IP source(s)' },
	{ label: 'Date began', value: ymd(item.date_range_start) },
	{ label: 'Date ended', value: ymd(item.date_range_end) },
	{ label: 'Advertiser concern', value: 'Search Network' },
	{ label: 'Further related to', value: 'Google Search Network' },
	{ label: 'Request type', value: 'Traffic Quality: Invalid clicks & refunds' },
	{ label: 'Recent opt-in', value: 'No' },
	{ label: 'Ads approved', value: 'Yes' },
	{ label: 'Budget increased', value: 'No' },
	{ label: 'Checked invalid clicks', value: 'Yes' },
	{ label: 'Response method', value: 'Email me' }
]

/** Status badge — Figma "Data Sheet" badge (node 6695:247398): dot + 14px label,
 *  tinted fill with a matching-color border, radius 16. */
const STATUS_BADGE: Record<
	string,
	{ label: string; color: string; bg: string; border: string }
> = {
	submitted: {
		label: 'Submitted',
		color: 'var(--t-trend-up)',
		bg: 'var(--t-badge-active)',
		border: 'var(--t-trend-up)'
	},
	failed: {
		label: 'Failed',
		color: 'var(--t-danger)',
		bg: 'var(--t-danger-warn)',
		border: 'var(--t-danger)'
	},
	no_evidence: {
		label: 'No evidence',
		color: 'var(--t-desc-gray)',
		bg: 'var(--bg-plan)',
		border: 'var(--t-checkbox)'
	}
}

const StatusBadge = ({ status }: { status: string }) => {
	const m = STATUS_BADGE[status] ?? {
		label: status,
		color: 'var(--t-desc-gray)',
		bg: 'var(--bg-plan)',
		border: 'var(--t-checkbox)'
	}
	return (
		<span
			className="inline-flex items-center gap-1.5 rounded-[16px] border py-1 pl-2.5 pr-3 text-[14px] leading-[18px] font-medium"
			style={{ color: m.color, backgroundColor: m.bg, borderColor: m.border }}
		>
			<span
				className="h-2 w-2 rounded-full"
				style={{ backgroundColor: m.color }}
			/>
			{m.label}
		</span>
	)
}

const Callout = ({
	title,
	children
}: {
	title: string
	children: ReactNode
}) => (
	<div className="rounded-[12px] border border-brand bg-t-step-inactive p-4 text-t-default">
		<p className="text-[14px] font-medium leading-[18px]">{title}</p>
		<div className="mt-1 text-[12px] leading-[16px] opacity-85">{children}</div>
	</div>
)

/** Count pill next to a section title (Figma "Form fields sent to Google 17"). */
const CountBadge = ({ count }: { count: number }) => (
	<span className="inline-flex h-[20px] min-w-[20px] items-center justify-center rounded-full bg-t-step-inactive px-1.5 text-[12px] font-semibold leading-4 text-brand-tint-text">
		{count}
	</span>
)

/**
 * Collapsible section card — Figma "Data Sheet" section (node 6695:246286):
 * bordered rounded-12 card, white header (title + subtitle + chevron, 16px pad)
 * over a `#fafafa` body (16px pad).
 */
const Section = ({
	title,
	subtitle,
	badge,
	defaultOpen = true,
	bodyClassName = 'p-4',
	children
}: {
	title: ReactNode
	subtitle?: ReactNode
	badge?: ReactNode
	defaultOpen?: boolean
	bodyClassName?: string
	children: ReactNode
}) => {
	const [open, setOpen] = useState(defaultOpen)
	return (
		<div className="overflow-hidden rounded-[12px] border border-t-checkbox">
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				className="flex w-full items-start gap-1.5 bg-white p-4 text-left"
			>
				<div className="flex min-w-0 flex-1 flex-col gap-1.5">
					<div className="flex items-center gap-2">
						<span className="text-[16px] leading-[22px] font-medium text-brand-tint-text-strong">
							{title}
						</span>
						{badge}
					</div>
					{subtitle && (
						<div className="text-[14px] leading-[18px]">{subtitle}</div>
					)}
				</div>
				<ChevronDown
					className={`h-6 w-6 shrink-0 text-t-gray-hold transition-transform ${
						open ? 'rotate-180' : ''
					}`}
				/>
			</button>
			{open && (
				<div
					className={`border-t border-t-checkbox bg-table-band-subtle ${bodyClassName}`}
				>
					{children}
				</div>
			)}
		</div>
	)
}

interface ViewSubmissionDrawerProps {
	open: boolean
	onClose: () => void
	item: RefundRequestDTO | null
}

/** §7 — View submission drawer (Submitted / Failed / No-evidence). Spec B7. */
export const ViewSubmissionDrawer = ({
	open,
	onClose,
	item
}: ViewSubmissionDrawerProps) => {
	const trackPostHogEvent = useTrackPostHogEvent()
	const [narrativeOpen, setNarrativeOpen] = useState(false)

	if (!item) return null

	const isNoEvidence = item.status === 'no_evidence'
	const isFailed = item.status === 'failed'
	const showFull = !isNoEvidence
	const campaigns = item.campaigns_affected || []
	const formFields = submittedFormFields(item)

	const handleDownload = () => {
		if (!item.evidence_csv_path) return
		trackPostHogEvent(PostHogEvent.DOWNLOAD, PostHogPath.REFUNDS_AGENT, {
			refund_request_id: item.id
		})
		window.open(item.evidence_csv_path, '_blank', 'noopener,noreferrer')
	}

	const narrative = item.narrative_text || ''
	const narrativeIsLong = narrative.length > 280

	return (
		<Sheet
			open={open}
			onClose={onClose}
			title="Submission details"
			style={REFUNDS_SHEET_FLOAT_STYLE}
		>
			{/* 24px inner padding comes from the Sheet body (p-6) = the card's own
			    padding; the 12px gap above is backdrop, not white. */}
			<div className="flex flex-col gap-4">
				{/* Top bar — Download + rows + status (Submitted & Failed only) */}
				{/* Submitted/Failed: Download + rows (left) · badge (right).
				    No-evidence: no download, so the lone badge sits left. */}
				<div className="flex items-center justify-between gap-3">
					{showFull && item.evidence_csv_path && (
						<div className="flex items-center gap-3">
							<AppButton
								variant="secondary"
								roundness="default"
								size="md"
								leftIcon={<Download01 className="h-4 w-4" />}
								label="Download .csv"
								onClick={handleDownload}
							/>
							<span className="text-[14px] leading-[18px] text-t-gray-hold">
								{item.invalid_click_count.toLocaleString()} rows
							</span>
						</div>
					)}
					<StatusBadge status={item.status} />
				</div>

				{isFailed && (
					<Callout title="Submission couldn't be completed">
						<p className="mb-3">
							We hit a temporary issue filing this refund with Google. Our team
							is already looking into it and will automatically re-submit the
							claim.
						</p>
						<p className="font-semibold">No action is needed on your end.</p>
					</Callout>
				)}

				{isNoEvidence && (
					<Callout title="No invalid clicks to report">
						<p>
							No qualifying invalid clicks were found between{' '}
							{fmtDate(item.date_range_start)} and {fmtDate(item.date_range_end)}
							, so there was nothing to claim and no submission was made.
							We&apos;ll file automatically once new invalid activity is
							detected.
						</p>
					</Callout>
				)}

				{/* Submission — always rendered */}
				<Section
					title="Submission"
					subtitle={
						<>
							<span className="text-t-desc-gray">Account ID · </span>
							<span className="font-medium text-t-coupon">
								{item.customer_id}
							</span>
						</>
					}
				>
					<div className="flex items-stretch">
						<div className="flex flex-1 flex-col items-center justify-center">
							<span className="text-[18px] leading-[28px] font-semibold text-t-coupon">
								{isNoEvidence
									? '–'
									: item.invalid_click_count.toLocaleString()}
							</span>
							<span className="text-[14px] leading-[18px] text-t-desc-gray">
								Invalid clicks
							</span>
						</div>
						<div className="w-px self-stretch bg-t-checkbox opacity-40" />
						<div className="flex flex-1 flex-col items-center justify-center">
							<span className="text-[18px] leading-[28px] font-semibold text-t-coupon">
								{isNoEvidence ? '–' : campaigns.length.toString()}
							</span>
							<span className="text-[14px] leading-[18px] text-t-desc-gray">
								Campaigns
							</span>
						</div>
					</div>
				</Section>

				{showFull && narrative && (
					<Section
						title="Narrative sent to Google"
						subtitle={
							<>
								<span className="text-t-desc-gray">Window · </span>
								<span className="font-medium text-t-coupon">
									{fmtDate(item.date_range_start)} –{' '}
									{fmtDate(item.date_range_end)}
								</span>
							</>
						}
					>
						<div className="flex flex-col gap-3">
							<div className="flex items-center gap-1.5">
								<Stars01 className="h-4 w-4 text-t-ai-accent" />
								<span className="text-[14px] leading-[18px] text-t-ai-accent">
									AI-generated summary based on actual click data
								</span>
							</div>
							<p
								className={`text-[14px] leading-[18px] text-t-desc-gray whitespace-pre-wrap ${
									!narrativeOpen && narrativeIsLong ? 'line-clamp-4' : ''
								}`}
							>
								{narrative}
							</p>
							{narrativeIsLong && (
								<button
									type="button"
									onClick={() => setNarrativeOpen((o) => !o)}
									className="flex items-center gap-1 text-[14px] font-medium text-brand-tint-text"
								>
									{narrativeOpen ? 'See less' : 'See more'}
									<ChevronDown
										className={`h-4 w-4 transition-transform ${
											narrativeOpen ? 'rotate-180' : ''
										}`}
									/>
								</button>
							)}
						</div>
					</Section>
				)}

				{showFull && (
					<Section
						title="Form fields sent to Google"
						badge={<CountBadge count={formFields.length} />}
						subtitle={
							<span className="text-t-desc-gray">
								Exact values typed into Google&apos;s Click Quality form
							</span>
						}
						bodyClassName="px-4 py-1.5"
					>
						<div className="flex flex-col">
							{formFields.map((f, i) => (
								<div key={f.label}>
									{i > 0 && <div className="h-px w-full bg-t-checkbox" />}
									<div className="flex items-start gap-3 py-[9px]">
										<span className="w-[150px] shrink-0 text-[14px] leading-[18px] text-t-desc-gray">
											{f.label}
										</span>
										<span className="flex-1 break-words text-right text-[14px] leading-[18px] font-medium text-t-coupon">
											{f.value}
										</span>
									</div>
								</div>
							))}
						</div>
					</Section>
				)}

				{showFull && campaigns.length > 0 && (
					<Section
						title="Campaigns"
						badge={<CountBadge count={campaigns.length} />}
						subtitle={
							<span className="text-t-desc-gray">
								Currently-enabled Google Ads campaigns with invalid clicks in
								this window
							</span>
						}
					>
						<ul className="flex flex-col gap-2">
							{campaigns.map((c) => (
								<li
									key={c}
									className="flex gap-2 text-[14px] leading-[18px] text-t-coupon"
								>
									<span className="text-brand-tint-text">•</span>
									{c}
								</li>
							))}
						</ul>
					</Section>
				)}

				<p className="text-[14px] leading-[18px] text-t-desc-gray">
					Visit the{' '}
					<a
						href={REFUNDS_AGENT_KB_URL}
						target="_blank"
						rel="noopener noreferrer"
						className="font-medium text-brand-tint-text"
					>
						Knowledge base
					</a>{' '}
					for additional help.
				</p>
			</div>
		</Sheet>
	)
}
